/**
 * NotificationPanel — List of notifications with mark-read and deep-link actions.
 *
 * Design: ADR-2 / design §2.6
 *
 * Accessibility:
 *   - role="list" / role="listitem" for screen readers.
 *   - aria-live="polite" for new notifications arriving.
 *   - Dismiss with Escape key.
 *   - No innerHTML with API data.
 *   - Empty state with descriptive text.
 *   - Error state with role="alert".
 */

import { useEffect, useRef } from "react";
import type { Notification } from "@autodieta/shared";

interface NotificationPanelProps {
  notifications: Notification[];
  loading: boolean;
  error?: string;
  /** Called when "Marcar como leída" is clicked. */
  onMarkRead: (id: string) => void;
  /** Called when closing the panel (Escape key or explicit close). */
  onClose: () => void;
  /** Called when a replan_proposal notification is clicked to deep-link. */
  onNavigateToProposal?: (refId: string) => void;
}

/** Format a relative time string from an ISO timestamp. */
function relativeTime(isoString: string): string {
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `hace ${diffHrs} h`;
  const diffDays = Math.floor(diffHrs / 24);
  return `hace ${diffDays} d`;
}

/** Type-label map for human-readable notification types (no API data injected). */
const TYPE_LABELS: Record<Notification["type"], string> = {
  replan_proposal: "Nueva propuesta de re-plan",
  check_in: "Check-in de adherencia",
  reminder: "Recordatorio",
};

export function NotificationPanel({
  notifications,
  loading,
  error,
  onMarkRead,
  onClose,
  onNavigateToProposal,
}: NotificationPanelProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);

  // Dismiss on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Focus the panel on open for keyboard users
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Panel de notificaciones"
      aria-modal="false"
      tabIndex={-1}
      className="absolute right-0 top-full z-50 mt-1 w-80 rounded-xl border border-zinc-700 bg-zinc-900 shadow-xl focus:outline-none"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
        <h2 className="text-sm font-semibold text-zinc-100">Notificaciones</h2>
        <button
          type="button"
          aria-label="Cerrar notificaciones"
          onClick={onClose}
          className="min-h-[36px] min-w-[36px] rounded-lg text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          ✕
        </button>
      </div>

      {/* Content area — aria-live for new items arriving */}
      <div
        aria-live="polite"
        aria-label="Lista de notificaciones"
        className="max-h-96 overflow-y-auto"
      >
        {/* Error state */}
        {error !== undefined ? (
          <div role="alert" className="px-4 py-6 text-center text-sm text-red-400">
            Error al cargar notificaciones.
          </div>
        ) : loading ? (
          <div aria-busy="true" className="px-4 py-6 text-center text-sm text-zinc-500">
            Cargando...
          </div>
        ) : notifications.length === 0 ? (
          /* Empty state */
          <div className="px-4 py-8 text-center text-sm text-zinc-500">
            No hay notificaciones.
          </div>
        ) : (
          /* Notification list */
          <ul role="list" className="divide-y divide-zinc-800">
            {notifications.map((notif) => (
              <li key={notif.id} role="listitem" className={`px-4 py-3 ${notif.read ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-3">
                  {/* Unread indicator */}
                  {!notif.read ? (
                    <span
                      aria-hidden="true"
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                    />
                  ) : (
                    <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0" />
                  )}

                  <div className="min-w-0 flex-1">
                    {/* Type label — from static map, NOT from API data */}
                    <p className="text-xs font-medium text-zinc-400">
                      {TYPE_LABELS[notif.type] ?? notif.type}
                    </p>
                    {/* Message — text content only, never innerHTML */}
                    <p className="mt-0.5 text-sm text-zinc-200">{notif.message}</p>
                    {/* Relative time */}
                    <p className="mt-1 text-xs text-zinc-500">
                      {relativeTime(notif.createdAt)}
                    </p>

                    {/* Actions */}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {/* Deep-link: replan_proposal → navigate to ProposalCard */}
                      {notif.type === "replan_proposal" &&
                      notif.refId !== undefined &&
                      onNavigateToProposal !== undefined ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (notif.refId !== undefined) {
                              onNavigateToProposal(notif.refId);
                            }
                            onClose();
                          }}
                          className="min-h-[32px] rounded-lg border border-zinc-600 px-3 text-xs text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                        >
                          Ver propuesta
                        </button>
                      ) : null}

                      {/* Mark as read */}
                      {!notif.read ? (
                        <button
                          type="button"
                          aria-label={`Marcar como leída: ${TYPE_LABELS[notif.type] ?? notif.type}`}
                          onClick={() => onMarkRead(notif.id)}
                          className="min-h-[32px] rounded-lg border border-zinc-700 px-3 text-xs text-zinc-400 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                        >
                          Marcar como leída
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
